import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  priceMonthly?: number;
  priceYearly?: number;
  currency: string;
  interval: 'month' | 'year';
  features: string[];
  displayFeatures?: string[]; // Features para mostrar en cards públicas
  displayFeaturesTranslations?: Partial<Record<'en' | 'pt', string[]>>;
  nameTranslations?: Partial<Record<'en' | 'pt', string>>;
  descriptionTranslations?: Partial<Record<'en' | 'pt', string>>;
  isActive: boolean;
  recommended?: boolean;
  description?: string;
  autoRenew?: boolean;
  durations?: { months: number; price: number }[];
  type?: 'standard' | 'enterprise';
  contactForm?: boolean;
  allowedContexts?: string[];
  limits?: Record<string, number>; // Límites del plan
}

export function useSubscriptionPlans() {
  const { i18n } = useTranslation();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const locale = (i18n.language || 'es').toLowerCase();

  const localizePlan = (plan: SubscriptionPlan): SubscriptionPlan => {
    if (!plan) return plan;
    const isEn = locale.startsWith('en');
    const isPt = locale.startsWith('pt');

    const name =
      (isEn ? plan?.nameTranslations?.en : isPt ? plan?.nameTranslations?.pt : plan.name) ||
      plan.name;
    const description =
      (isEn ? plan?.descriptionTranslations?.en : isPt ? plan?.descriptionTranslations?.pt : plan.description) ||
      plan.description;
    const displayFeatures =
      (isEn ? plan?.displayFeaturesTranslations?.en : isPt ? plan?.displayFeaturesTranslations?.pt : plan.displayFeatures) ||
      plan.displayFeatures;

    return {
      ...plan,
      name,
      description,
      displayFeatures,
    };
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "subscriptionPlans"),
      (snapshot) => {
        if (snapshot.empty) {
          // Valores por defecto si no hay planes en Firestore
          const defaults: SubscriptionPlan[] = [
            {
              id: 'free',
              name: 'Plan Gratuito',
              price: 0,
              currency: 'USD',
              interval: 'month',
              description: 'Perfecto para empezar',
              features: [
                'Hasta 3 miembros',
                'Actividades básicas',
                'Checklist simple',
                'Calendario básico',
              ],
              isActive: true,
            },
            {
              id: 'familiar',
              name: 'Plan Familiar',
              price: 9.99,
              currency: 'USD',
              interval: 'month',
              description: 'Ideal para familias',
              features: [
                'Miembros ilimitados',
                'Todas las actividades',
                'Checklist avanzado',
                'Calendario completo',
                'Reportes y estadísticas',
                'Soporte prioritario',
              ],
              isActive: true,
              recommended: true,
            },
          ];

          setPlans(defaults.map(localizePlan));
        } else {
          const plansData = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() } as SubscriptionPlan))
            .filter(plan => plan.isActive)
            .sort((a, b) => a.price - b.price);
          setPlans(plansData.map(localizePlan));
        }
        setLoading(false);
      },
      (err) => {
        setError("Error al cargar los planes");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [locale]);

  return { plans, loading, error };
}
